import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AuditTrail } from "../../components/detail/auditTrail";
import { User } from "../../schema/types/user/entity";
import { Summary } from "./summary";
import { UserDetailHeader } from "./header";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

interface Props {
  user: User;
  refetch?: () => void;
}
export const UserDetailContent: React.FC<Props> = ({ user, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/user/:idOrEmail/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(user).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(user.lastModified);
  }, [user.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary"),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <UserDetailHeader user={user} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary user={user} />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"user"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={<AuditTrail kind={"user"} itemName={user.name} resourceId={user.id} version={user.version} selfLink={linksOf(user).self!} refetch={refetch} />}
              />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
